/**
 * SignaturePad — tiny canvas-based signature capture used by Phase E's
 * Final Review dialog.
 *
 * • Draws with the pointer / touch (single-pointer capture).
 * • Smooth strokes via quadratic-Bezier midpoint smoothing — softens
 *   pointer jitter without introducing lag.
 * • Emits the current signature as an image/png data-URL via `onChange`.
 * • Exposes a `clear()` action via ref so the parent dialog can reset.
 * • Shows an inline hint on coarse-pointer devices ("Touch device?
 *   Switch to Type above.") so mobile signers know they have a
 *   keyboard fallback.
 * • Zero external dependencies — HTML5 canvas + pointer events.
 */
import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from "react";
import { Eraser, Keyboard } from "@phosphor-icons/react";

const DEFAULT_HEIGHT = 140;
const STROKE_STYLE   = "#0f172a";  // slate-900
const STROKE_WIDTH   = 2.2;

const SignaturePad = forwardRef(function SignaturePad(
  { onChange, height = DEFAULT_HEIGHT, disabled = false, testId = "signature-pad" },
  ref,
) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef  = useRef(null);
  const midPtRef   = useRef(null);
  const [empty, setEmpty] = useState(true);
  const [coarsePointer, setCoarsePointer] = useState(false);

  // Detect coarse-pointer (touch-primary) devices so we can offer a
  // keyboard hint. `matchMedia("(pointer: coarse)")` is the modern,
  // reliable indicator — trumps `ontouchstart` / userAgent sniffing.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(pointer: coarse)");
    const apply = () => setCoarsePointer(!!mql.matches);
    apply();
    // Both addEventListener + addListener for old-Safari compatibility.
    try { mql.addEventListener("change", apply); }
    catch { mql.addListener?.(apply); }
    return () => {
      try { mql.removeEventListener("change", apply); }
      catch { mql.removeListener?.(apply); }
    };
  }, []);

  // Fit the canvas to its container width on mount + resize.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const fit = () => {
      const dpr = window.devicePixelRatio || 1;
      const cssW = canvas.clientWidth;
      const cssH = height;
      if (canvas.width !== cssW * dpr || canvas.height !== cssH * dpr) {
        // Preserve the drawing on resize by snapshotting first.
        const snapshot = empty
          ? null
          : canvas.toDataURL("image/png");
        canvas.width  = cssW * dpr;
        canvas.height = cssH * dpr;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.lineJoin = "round";
        ctx.lineCap  = "round";
        ctx.strokeStyle = STROKE_STYLE;
        ctx.lineWidth   = STROKE_WIDTH;
        if (snapshot) {
          const img = new Image();
          img.onload = () => ctx.drawImage(img, 0, 0, cssW, cssH);
          img.src = snapshot;
        }
      }
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [height, empty]);

  const emit = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onChange) return;
    onChange(empty ? "" : canvas.toDataURL("image/png"));
  }, [onChange, empty]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineJoin = "round";
    ctx.lineCap  = "round";
    ctx.strokeStyle = STROKE_STYLE;
    ctx.lineWidth   = STROKE_WIDTH;
    ctx.restore();
    setEmpty(true);
    lastPtRef.current = null;
    if (onChange) onChange("");
  }, [onChange]);

  useImperativeHandle(ref, () => ({ clear }), [clear]);

  const localPoint = (evt) => {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    return {
      x: (evt.clientX ?? evt.touches?.[0]?.clientX) - rect.left,
      y: (evt.clientY ?? evt.touches?.[0]?.clientY) - rect.top,
    };
  };

  const onPointerDown = (evt) => {
    if (disabled) return;
    evt.preventDefault();
    drawingRef.current = true;
    const pt = localPoint(evt);
    lastPtRef.current = pt;
    midPtRef.current  = pt;
    setEmpty(false);
    canvasRef.current?.setPointerCapture?.(evt.pointerId);
    // Kick a dot in case the user just taps.
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, STROKE_WIDTH / 2, 0, Math.PI * 2);
      ctx.fillStyle = STROKE_STYLE;
      ctx.fill();
    }
  };
  const onPointerMove = (evt) => {
    if (!drawingRef.current || disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const pt  = localPoint(evt);
    // Quadratic-Bezier midpoint smoothing — for each new point we draw
    // a curve using the previous point as the control and the midpoint
    // between prev + current as the endpoint. This averages away
    // pointer jitter without introducing lag.
    const last = lastPtRef.current;
    if (last) {
      const mid = { x: (last.x + pt.x) / 2, y: (last.y + pt.y) / 2 };
      ctx.beginPath();
      ctx.moveTo(midPtRef.current.x, midPtRef.current.y);
      ctx.quadraticCurveTo(last.x, last.y, mid.x, mid.y);
      ctx.stroke();
      midPtRef.current = mid;
    }
    lastPtRef.current = pt;
  };
  const onPointerUp = () => {
    if (!drawingRef.current) return;
    // Finalise: draw the last stroke tail up to the very last pointer
    // sample so short taps end at the actual finger-up location.
    const canvas = canvasRef.current;
    if (canvas && lastPtRef.current && midPtRef.current) {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.beginPath();
      ctx.moveTo(midPtRef.current.x, midPtRef.current.y);
      ctx.lineTo(lastPtRef.current.x, lastPtRef.current.y);
      ctx.stroke();
    }
    drawingRef.current = false;
    lastPtRef.current  = null;
    midPtRef.current   = null;
    emit();
  };

  return (
    <div className="rounded-lg border border-slate-300 bg-white overflow-hidden"
                data-testid={testId}>
      <canvas
        ref={canvasRef}
        style={{ height, width: "100%",
                    touchAction: "none",
                    cursor: disabled ? "not-allowed" : "crosshair" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        data-testid={`${testId}-canvas`}
      />
      <div className="flex items-center justify-between px-2 py-1 border-t border-slate-200 bg-slate-50 text-[10.5px] text-slate-500">
        <span>
          {empty
            ? "Draw your signature above · pointer / stylus supported"
            : "Signature captured — data-URL PNG"}
        </span>
        <button
          type="button" onClick={clear} disabled={empty || disabled}
          data-testid={`${testId}-clear`}
          className="flex items-center gap-1 text-slate-500 hover:text-rose-600 disabled:opacity-40"
        >
          <Eraser size={11} /> Clear
        </button>
      </div>
      {coarsePointer && (
        <div
          className="px-2 py-1 border-t border-slate-200 bg-amber-50 text-[10.5px] text-amber-800 flex items-center gap-1"
          data-testid={`${testId}-touch-hint`}
        >
          <Keyboard size={11} weight="fill" />
          Touch device? Switch to <span className="font-semibold">Type</span> above
          for keyboard input — both are accepted and hashed the same way.
        </div>
      )}
    </div>
  );
});

export default SignaturePad;
